import { useState } from "react";
import { Documento, Caso, AnalisisEstudio, CasoPropiedad, Propiedad, Reclamo, Cobro, Tesoreria, Usuario } from "../../types";
import { Badge } from "../common/Badge";

interface DocumentosClienteTabProps {
  clienteId: number;
  propiedades: Propiedad[];
  documentos: Documento[];
  casos: Caso[];
  analisisEstudios: AnalisisEstudio[];
  casoPropiedades: CasoPropiedad[];
  reclamos: Reclamo[];
  cobros: Cobro[];
  tesoreriaItems: Tesoreria[];
  usuarios: Usuario[];
}

type OrigenDoc = "estudio" | "solicitud" | "propiedad" | "otro";

interface DocConOrigen {
  doc: Documento;
  origen: OrigenDoc;
  origenLabel: string;
}

export function DocumentosClienteTab({
  clienteId,
  propiedades,
  documentos,
  casos,
  analisisEstudios,
  casoPropiedades,
  reclamos: _reclamos,
  cobros: _cobros,
  tesoreriaItems: _tesoreriaItems,
  usuarios,
}: DocumentosClienteTabProps) {
  const [filterOrigen, setFilterOrigen] = useState<OrigenDoc | "all">("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Get property IDs for this cliente
  const propiedadIds = propiedades.filter((p) => p.clienteId === clienteId).map((p) => p.id);

  // Get caso IDs linked to these properties via casoPropiedades
  const casoIds = Array.from(
    new Set(
      casoPropiedades
        .filter((cp) => propiedadIds.includes(cp.propiedadId))
        .map((cp) => cp.casoId)
    )
  );

  // Get analisisEstudio IDs linked via cases
  const analisisIds = Array.from(
    new Set(
      casos
        .filter((c) => casoIds.includes(c.id) && c.analisisEstudioId)
        .map((c) => c.analisisEstudioId!)
    )
  );

  // Also find analisis that directly reference this client's properties
  // (some analisis might not have a case yet)

  // Build document list with origin labels
  const docsConOrigen: DocConOrigen[] = [];

  documentos.forEach((doc) => {
    let matched = false;

    // Docs linked to a property of this cliente
    if (doc.propiedadId && propiedadIds.includes(doc.propiedadId)) {
      const prop = propiedades.find((p) => p.id === doc.propiedadId);
      docsConOrigen.push({
        doc,
        origen: "propiedad",
        origenLabel: `ROL ${prop?.rol || "?"} - ${prop?.comuna || "?"}`,
      });
      matched = true;
    }

    // Docs linked to a caso of this cliente's properties
    if (!matched && doc.casoId && casoIds.includes(doc.casoId)) {
      const caso = casos.find((c) => c.id === doc.casoId);
      docsConOrigen.push({
        doc,
        origen: "solicitud",
        origenLabel: caso?.titulo || `Solicitud #${doc.casoId}`,
      });
      matched = true;
    }

    // Docs linked to an analisis of this cliente
    if (!matched && doc.analisisEstudioId && analisisIds.includes(doc.analisisEstudioId)) {
      const analisis = analisisEstudios.find((a) => a.id === doc.analisisEstudioId);
      docsConOrigen.push({
        doc,
        origen: "estudio",
        origenLabel: analisis?.titulo || `Estudio #${doc.analisisEstudioId}`,
      });
      matched = true;
    }
  });

  // Apply filters
  const filteredDocs = docsConOrigen.filter((d) => {
    const matchesOrigen = filterOrigen === "all" || d.origen === filterOrigen;
    const matchesSearch =
      !searchTerm ||
      d.doc.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.doc.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.origenLabel.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesOrigen && matchesSearch;
  });

  // Sort by date descending
  filteredDocs.sort((a, b) => b.doc.fechaSubida.localeCompare(a.doc.fechaSubida));

  const getUsuarioNombre = (id: number) =>
    usuarios.find((u) => u.id === id)?.nombre || "?";

  const getOrigenBadgeVariant = (origen: OrigenDoc) => {
    const variants: Record<OrigenDoc, "info" | "purple" | "success" | "neutral"> = {
      estudio: "info",
      solicitud: "purple",
      propiedad: "success",
      otro: "neutral",
    };
    return variants[origen];
  };

  const getOrigenLabel = (origen: OrigenDoc) => {
    const labels: Record<OrigenDoc, string> = {
      estudio: "Estudio",
      solicitud: "Solicitud",
      propiedad: "Propiedad",
      otro: "Otro",
    };
    return labels[origen];
  };

  if (propiedadIds.length === 0) {
    return (
      <div className="text-center py-6 text-slate-400 text-sm">
        Este cliente no tiene propiedades asociadas
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          Documentos ({filteredDocs.length})
        </h3>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <input
          type="text"
          placeholder="Buscar documento..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filterOrigen}
          onChange={(e) => setFilterOrigen(e.target.value as OrigenDoc | "all")}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Todos los origenes</option>
          <option value="estudio">Estudios</option>
          <option value="solicitud">Solicitudes</option>
          <option value="propiedad">Propiedades</option>
        </select>
      </div>

      {/* Document list */}
      {filteredDocs.length > 0 ? (
        <div className="space-y-2">
          {filteredDocs.map(({ doc, origen, origenLabel }) => (
            <div
              key={doc.id}
              className="p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-medium text-sm text-slate-800 truncate">
                      {doc.nombre}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 ml-6 flex-wrap">
                    <Badge variant={getOrigenBadgeVariant(origen)}>
                      {getOrigenLabel(origen)}
                    </Badge>
                    <span className="text-xs text-slate-500 truncate" title={origenLabel}>
                      {origenLabel}
                    </span>
                  </div>
                </div>
                <div className="text-right text-xs text-slate-500 flex-shrink-0 ml-3">
                  <p>{doc.fechaSubida.split("T")[0]}</p>
                  <p>{getUsuarioNombre(doc.subidoPor)}</p>
                  <p className="text-slate-400">{doc.tamano}</p>
                </div>
              </div>
              {doc.clasificacion && (
                <p className="text-xs text-slate-400 ml-6 mt-1">
                  {doc.clasificacion}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400 text-sm">
          {searchTerm || filterOrigen !== "all"
            ? "No se encontraron documentos con los filtros aplicados"
            : "No hay documentos asociados a este cliente"}
        </div>
      )}
    </div>
  );
}
